import React, { useState, useEffect } from "react";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import {
  useLoginApiMutation,
  useResetPasswordApiMutation,
} from "@/services/api/auth/authApi";
import { useNavigate } from "react-router-dom";
import { message, Progress } from "antd"; // Import message and Progress from antd for consistent UI

// ✅ Moved outside so it doesn't remount on every render
const PasswordInput = ({
  label,
  name,
  value,
  onChange,
  show,
  onToggle,
  placeholder,
  error,
  passwordStrength, // Pass passwordStrength for new password field
  showStrengthIndicator = false, // New prop to conditionally show strength
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">
      {label}
    </label>
    <div className="relative">
      <input
        type={show ? "text" : "password"}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`w-full px-2 py-3 pr-10 bg-gray-50 border rounded-lg focus:ring-2 focus:ring-orange-300 focus:border-transparent outline-none transition-all ${
          error ? "border-red-500" : "border-gray-200"
        }`}
      />
      <button
        type="button"
        onClick={onToggle}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
      >
        {show ? <EyeOutlined /> : <EyeInvisibleOutlined />}
      </button>
    </div>
    {error && <p className="mt-1 text-xs text-red-600">{error}</p>}

    {/* Password Strength Indicator */}
    {showStrengthIndicator && value && passwordStrength && (
      <div className="mt-2">
        <Progress
          percent={passwordStrength.percentage}
          strokeColor={passwordStrength.color}
          showInfo={false}
          strokeWidth={6}
          className="mb-1"
        />
        {passwordStrength.score > 0 && (
          <div className="flex items-center gap-2">
            {passwordStrength.score === 1 && (
              <>
                <span className="text-xl">😕</span>
                <span className="text-xs text-gray-600">
                  Weak: Add uppercase, lowercase, numbers & special characters
                </span>
              </>
            )}
            {passwordStrength.score === 2 && (
              <>
                <span className="text-xl">😐</span>
                <span className="text-xs text-gray-600">
                  Medium: Try adding special characters (!@#$%) for a stronger
                  password
                </span>
              </>
            )}
            {passwordStrength.score === 3 && (
              <>
                <span className="text-xl">😊</span>
                <span className="text-xs text-green-600 font-medium">
                  Strong: Great password!
                </span>
              </>
            )}
          </div>
        )}
      </div>
    )}
  </div>
);

const AdminLogin = () => {
  const [currentPage, setCurrentPage] = useState("login");
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    resetEmail: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});

  // State for password strength
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    label: "",
    color: "",
    percentage: 0,
  });

  const [loginApi, { isLoading: loginLoading }] = useLoginApiMutation();
  const [resetPasswordApi, { isLoading: resetLoading }] =
    useResetPasswordApiMutation();
  const navigate = useNavigate();

  // Calculate password strength - moved here for AdminLogin component
  const calculatePasswordStrength = (pwd) => {
    let score = 0;
    const checks = {
      minLength: pwd.length >= 8,
      hasUpperCase: /[A-Z]/.test(pwd),
      hasLowerCase: /[a-z]/.test(pwd),
      hasNumber: /[0-9]/.test(pwd),
      hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd),
    };

    Object.values(checks).forEach((check) => {
      if (check) score++;
    });

    let strength = {
      score: 0,
      label: "",
      color: "",
      percentage: 0,
    };

    if (score === 0) {
      strength = { score: 0, label: "", color: "", percentage: 0 };
    } else if (score <= 2) {
      strength = { score: 1, label: "Weak", color: "#ff4d4f", percentage: 33 };
    } else if (score <= 4) {
      strength = {
        score: 2,
        label: "Medium",
        color: "#faad14",
        percentage: 66,
      };
    } else {
      strength = {
        score: 3,
        label: "Strong",
        color: "#52c41a",
        percentage: 100,
      };
    }

    setPasswordStrength(strength);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "newPassword") {
      calculatePasswordStrength(value);
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validateLogin = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.email?.trim()) {
      newErrors.email = "Please enter your email address.";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email address.";
    }

    if (!formData.password?.trim()) {
      newErrors.password = "Please enter your password.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateReset = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.resetEmail?.trim()) {
      newErrors.resetEmail = "Please enter your email address.";
    } else if (!emailRegex.test(formData.resetEmail)) {
      newErrors.resetEmail = "Please enter a valid email address.";
    }

    if (!formData.newPassword?.trim()) {
      newErrors.newPassword = "Please enter a new password.";
    } else if (passwordStrength.score < 2) {
      newErrors.newPassword = "Please create a stronger password.";
    }

    if (!formData.confirmPassword?.trim()) {
      newErrors.confirmPassword = "Please confirm your password.";
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!validateLogin()) return;

    try {
      await loginApi({
        email: formData.email,
        password: formData.password,
      }).unwrap();

      navigate("/admin/dashboard");
    } catch {
      // Error handled in slice
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!validateReset()) {
      message.warning("Please correct the errors in the form."); // Ant Design message
      return;
    }

    try {
      await resetPasswordApi({
        email: formData.resetEmail,
        password: formData.newPassword,
        confirm_password: formData.confirmPassword,
      }).unwrap();

      message.success("Password reset successfully!"); // Ant Design message
      setTimeout(() => {
        setFormData((prev) => ({
          ...prev,
          resetEmail: "",
          newPassword: "",
          confirmPassword: "",
        }));
        setPasswordStrength({ score: 0, label: "", color: "", percentage: 0 }); // Reset strength
        setCurrentPage("login");
      }, 2000);
    } catch (error) {
      message.error(
        error?.data?.message || "Failed to reset password. Please try again.",
      ); // Ant Design message
    }
  };

  return (
    <div className="h-full flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        {currentPage === "login" ? (
          <>
            <div className="text-center mb-8">
              <h2 className="text-xl text-gray-700 flex items-center justify-center gap-2">
                Welcome back <span className="text-2xl">👋</span>
              </h2>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Email"
                  className={`w-full px-2 py-3 bg-gray-50 border rounded-lg focus:ring-2 focus:ring-orange-300 focus:border-transparent outline-none transition-all ${
                    errors.email ? "border-red-500" : "border-gray-200"
                  }`}
                />
                {errors.email && (
                  <p className="mt-1 text-xs text-red-600">{errors.email}</p>
                )}
              </div>

              <PasswordInput
                label="Enter Password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                show={showPassword}
                onToggle={() => setShowPassword(!showPassword)}
                placeholder="Password"
                error={errors.password}
              />

              <button
                type="submit"
                disabled={loginLoading}
                className="w-full bg-[#F59B1A] hover:bg-orange-300 text-white font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loginLoading ? "Signing in..." : "Login"}
              </button>
            </form>

            <div className="text-center mt-4">
              <button
                type="button"
                onClick={() => {
                  setCurrentPage("reset");
                  setErrors({}); // Clear errors when switching pages
                  setFormData((prev) => ({
                    ...prev,
                    resetEmail: "",
                    newPassword: "",
                    confirmPassword: "",
                  }));
                  setPasswordStrength({
                    score: 0,
                    label: "",
                    color: "",
                    percentage: 0,
                  }); // Reset strength
                }}
                className="text-sm text-gray-600 hover:text-green-600 transition-colors"
              >
                Forgot password?{" "}
                <span className="text-green-600 font-medium">Reset here</span>
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="text-center mb-8">
              <h2 className="text-xl text-gray-700">Reset Password</h2>
              <p className="text-sm text-gray-500 mt-2">
                Enter your details to reset your password
              </p>
            </div>

            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email address
                </label>
                <input
                  type="email"
                  name="resetEmail"
                  value={formData.resetEmail}
                  onChange={handleInputChange}
                  placeholder="Enter admin email"
                  className={`w-full px-2 py-3 bg-gray-50 border rounded-lg focus:ring-2 focus:ring-orange-300 focus:border-transparent outline-none transition-all ${
                    errors.resetEmail ? "border-red-500" : "border-gray-200"
                  }`}
                />
                {errors.resetEmail && (
                  <p className="mt-1 text-xs text-red-600">
                    {errors.resetEmail}
                  </p>
                )}
              </div>

              <PasswordInput
                label="New Password"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleInputChange}
                show={showNewPassword}
                onToggle={() => setShowNewPassword(!showNewPassword)}
                placeholder="Enter new password"
                error={errors.newPassword}
                passwordStrength={passwordStrength} // Pass strength for new password
                showStrengthIndicator={true} // Show strength for new password
              />

              <PasswordInput
                label="Confirm Password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                show={showConfirmPassword}
                onToggle={() => setShowConfirmPassword(!showConfirmPassword)}
                placeholder="Confirm new password"
                error={errors.confirmPassword}
              />

              <button
                type="submit"
                disabled={resetLoading}
                className="w-full bg-[#F59B1A] hover:bg-orange-300 text-white font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resetLoading ? "Resetting Password..." : "Reset Password"}
              </button>
            </form>

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => {
                  setCurrentPage("login");
                  setErrors({}); // Clear errors when switching pages
                  setFormData((prev) => ({
                    ...prev,
                    resetEmail: "",
                    newPassword: "",
                    confirmPassword: "",
                  }));
                  setPasswordStrength({
                    score: 0,
                    label: "",
                    color: "",
                    percentage: 0,
                  }); // Reset strength
                }}
                className="text-sm text-gray-600 hover:text-green-600 transition-colors"
              >
                ← Back to login
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminLogin;
